import React from 'react';

import { Box, Button, Divider, Grid, Typography } from '@mui/material';

import { Form } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';

import {
  ActiveInactive,
  ConsumerDocument,
  ConsumerQuery,
  StatesItemFragment,
  useSendDeleteMyDataRequestMutation,
} from 'src/api';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { EntityActionConfirmationModal } from 'src/modals/common';

import { containerStyles } from './styles';

interface DeleteYourDataProps {
  consumer: Nullable<ConsumerQuery['employee']>;
  states: StatesItemFragment[];
}

export const DeleteYourData: React.FC<DeleteYourDataProps> = ({ consumer, states }) => {
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();
  const { openModal } = useModalV2();

  // boolean state which is used to disable button after request is successfully sent
  const [isRequestSuccessful, setIsRequestSuccessful] = React.useState(false);

  const [sendDeleteMyDataRequest] = useSendDeleteMyDataRequestMutation({
    refetchQueries: [ConsumerDocument],
    context: createApolloContext({
      successMessage: 'The changes have been saved',
    }),
  });

  const onSubmit = React.useCallback(async () => {
    if (!consumer?.id) return;
    try {
      await sendDeleteMyDataRequest();
      setIsRequestSuccessful(true);
    } catch (error) {
      if (error instanceof Error) {
        console.error(error);
      }
    }
  }, [consumer?.id, sendDeleteMyDataRequest]);

  const isDeleteRequestSent = React.useMemo(
    () => !!consumer?.deleteRequestSentAt,
    [consumer?.deleteRequestSentAt],
  );

  const handleOpenDeleteDataModal = React.useCallback(
    (onSubmit: () => void) => {
      openModal(EntityActionConfirmationModal, {
        id: ModalId.EntityActionConfirmation,
        onSubmit,
        action: 'delete',
        entity: 'data',
        actionButtonColor: 'error',
      });
    },
    [openModal],
  );

  if (!states[0]?.deleteRequest || states[0].deleteRequest === ActiveInactive.Inactive) {
    return null;
  }

  return (
    <Box sx={containerStyles}>
      <Box mb={3}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Delete your Data</Typography>
        <Typography variant="body1" color="text.secondary" mt={1}>
          According to compliance regulations, you are able to submit a request for deleting your
          data. After deletion, your account will be fully deactivated and your data will be removed
          from the platform. If you wish to use Clear Verify in any capacity after data deletion,
          there is a complex process that must be adhered to in order to reintroduce your data to
          our platform. For this reason, we encourage you to consider our “Freeze Account”
          functionality as an alternative to data deletion.
        </Typography>
      </Box>
      <Divider />
      <Form onSubmit={onSubmit} subscription={{ submitting: true }}>
        {({ submitting, handleSubmit }) => (
          <Grid container mt={3}>
            <Grid display="flex" justifyContent="center" alignItems="center" gap={2} size={12}>
              <Button
                type="button"
                color={isDeleteRequestSent ? 'secondary' : 'error'}
                variant="outlined"
                disabled={submitting || isDeleteRequestSent || isRequestSuccessful}
                loading={submitting}
                onClick={() => handleOpenDeleteDataModal(handleSubmit)}
              >
                {isDeleteRequestSent ? 'Request sent' : 'Delete my data'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Form>
    </Box>
  );
};
